import { create } from 'zustand';
import type {
  StakeArtifacts,
  StakeReport,
  StakeRoundExplain,
  StakeStage,
} from '@sr-test-wizard/shared';
import { stakeEngineApi, type VerifyBody } from '../lib/stakeEngineApi';

/**
 * Live progress snapshot for one stake-engine stage (verify / chat), fed from
 * the `stake-engine-progress` WS stream. `tool` is the last gate / file
 * operation label so the page can show an activity line.
 */
export interface StakeStageProgress {
  status: 'started' | 'progress' | 'completed' | 'failed';
  message?: string;
  tool?: string;
}

interface StakeEngineState {
  runId: string | null;
  report: StakeReport | null;
  explain: StakeRoundExplain | null;
  /** Per-mode busy flags for the on-demand AI round analysis. */
  explainBusy: Record<string, boolean>;
  /** Per-mode error from the last AI round analysis attempt. */
  explainError: Record<string, string>;
  loading: boolean;
  error: string | null;
  progress: Partial<Record<StakeStage, StakeStageProgress>>;

  load: (runId: string) => Promise<void>;
  verify: (body: VerifyBody) => Promise<void>;
  generateExplain: (mode: string) => Promise<void>;
  applyProgress: (stage: StakeStage, snapshot: StakeStageProgress) => void;
  refetch: () => Promise<void>;
  clear: () => void;
}

export const useStakeEngineStore = create<StakeEngineState>((set, get) => ({
  runId: null,
  report: null,
  explain: null,
  explainBusy: {},
  explainError: {},
  loading: false,
  error: null,
  progress: {},

  async load(runId) {
    const isDifferent = get().runId !== null && get().runId !== runId;
    set({
      runId,
      loading: true,
      error: null,
      ...(isDifferent ? { report: null, explain: null, explainBusy: {}, explainError: {}, progress: {} } : {}),
    });
    try {
      const data: StakeArtifacts = await stakeEngineApi.get(runId);
      set({ report: data.report, explain: data.explain });
    } catch (err) {
      set({ error: (err as Error).message });
    } finally {
      set({ loading: false });
    }
  },

  async generateExplain(mode) {
    const { runId } = get();
    if (!runId) return;
    set((s) => ({
      explainBusy: { ...s.explainBusy, [mode]: true },
      explainError: { ...s.explainError, [mode]: '' },
    }));
    try {
      const data = await stakeEngineApi.explain(runId, mode);
      set({ explain: data.explain });
    } catch (err) {
      set((s) => ({ explainError: { ...s.explainError, [mode]: (err as Error).message } }));
    } finally {
      set((s) => ({ explainBusy: { ...s.explainBusy, [mode]: false } }));
    }
  },

  async verify(body) {
    const { runId } = get();
    if (!runId) return;
    set((s) => ({
      progress: { ...s.progress, verify: { status: 'started' } },
      error: null,
    }));
    try {
      await stakeEngineApi.verify(runId, body);
    } catch (err) {
      set({ error: (err as Error).message });
    }
  },

  applyProgress(stage, snapshot) {
    set((s) => ({ progress: { ...s.progress, [stage]: snapshot } }));
    if (stage === 'verify' && (snapshot.status === 'completed' || snapshot.status === 'failed')) {
      void get().refetch();
    }
  },

  async refetch() {
    const { runId } = get();
    if (!runId) return;
    try {
      const data = await stakeEngineApi.get(runId);
      set({ report: data.report, explain: data.explain });
    } catch {
      // keep existing state; WS already logged the error
    }
  },

  clear() {
    set({
      runId: null,
      report: null,
      explain: null,
      explainBusy: {},
      explainError: {},
      loading: false,
      error: null,
      progress: {},
    });
  },
}));
